import { Checkmark32 } from '@packages/themes/icons';
import { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useAtom } from 'jotai';
import {
  Image,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  Divider,
  Stack,
  Typography,
} from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { ServiceLayer } from '@packages/modules';
import { useTracking } from '@packages/tracking';
import type { Product } from '@packages/gql/generated/shopping/graphql';
import type { SelectedService } from '../../atoms';
import { selectedServices } from '../../atoms';
import { useFormatedPrice } from '../../hooks/useFormatedPrice/useFormatedPrice';

export const messages = defineMessages({
  articleServicesCardHeadline: {
    id: 'article.services.card.headline',
    defaultMessage: 'Services jetzt dazu bestellen',
  },
  articleServicesCardTip: {
    id: 'article.services.card.tip',
    defaultMessage: 'Tipp',
  },
  articleServicesMoreInfo: {
    id: 'article.services.more.text',
    defaultMessage: 'Mehr Infos',
  },
  articleServicesInclusive: {
    id: 'article.services.inclusive',
    defaultMessage: 'inklusive',
  },
  articleServicesFree: {
    id: 'article.services.free',
    defaultMessage: 'gratis',
  },
});

const ArticleServicesFragment = graphql(/* GraphQL */ `
  fragment ArticleServicesFragment on APlus {
    clusters {
      label
      services {
        mandatory
        gratis
        name
        preselected
        price
        serviceIcon
        uuid
      }
    }
  }
`);

type ArticleServicesProps = {
  aPlusData: FragmentType<typeof ArticleServicesFragment>;
  /* should be in aPlus price value */
  currency: Product['price']['currency'];
};

export const ArticleServices = ({ aPlusData, currency }: ArticleServicesProps) => {
  const { clusters } = getFragmentData(ArticleServicesFragment, aPlusData);
  const { formatMessage } = useIntl();
  const formatPrice = useFormatedPrice('APLUS');
  const [activeArticleService, setActiveArticleService] = useState(clusters[0]?.services[0]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const dispatchGTMEvent = useTracking();
  const [selected, setSelected] = useAtom(selectedServices);

  useEffect(() => {
    setSelected(
      clusters.reduce((acc, cluster) => {
        // TODO(api-layer): check if c needs to be nullable! (clusters array value type)
        if (cluster) {
          cluster.services.forEach((service) => {
            if (service.preselected) {
              // TODO(api-layer): check service data structure
              acc.push({ sku: service.uuid, selected: true });
            }
          });
        }
        return acc;
      }, [] as Array<SelectedService>),
    );
  }, [clusters, setSelected]);

  if (clusters.length === 0) {
    return null;
  }

  const handleClick = (service: typeof activeArticleService) => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'viewLayer',
      eventCategory: 'serviceLayer',
      eventLabel: 'detailview',
      eventValue: service?.name || '',
    });
    setIsModalOpen(true);
    setActiveArticleService(service);
  };

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Chip
              label={
                <Typography
                  variant="body3"
                  color="common.white"
                  sx={{ textTransform: 'uppercase' }}
                >
                  {formatMessage(messages.articleServicesCardTip)}
                </Typography>
              }
              color="success"
              sx={{ height: 'auto' }}
            />
            <Typography variant="h5">
              {formatMessage(messages.articleServicesCardHeadline)}
            </Typography>
          </Stack>
        }
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <Stack spacing={1} divider={<Divider />}>
          {clusters.map(
            (cluster) =>
              cluster && (
                <Stack key={cluster.label} gap={1}>
                  <Typography
                    variant="body3"
                    fontWeight="fontWeightSemiBold"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {cluster.label}
                  </Typography>
                  {cluster.services.map((service) => {
                    const { uuid, gratis, mandatory, serviceIcon, name, price } = service;

                    return (
                      <Stack key={uuid} direction="row" alignItems="flex-start">
                        <Box
                          sx={{ width: '2.5rem', height: '1.75rem' }}
                          display="flex"
                          alignItems="center"
                        >
                          {mandatory && <Checkmark32 fontSize="medium" color="success" />}
                          {!mandatory && (
                            <Checkbox
                              sx={{ padding: 0 }}
                              checked={selected.some((s) => s.sku === uuid)}
                              onChange={(e) =>
                                setSelected(
                                  e.target.checked
                                    ? [...selected, { sku: uuid, selected: true }]
                                    : selected.filter((s) => s.sku !== uuid),
                                )
                              }
                            />
                          )}
                        </Box>

                        <Stack direction="row" gap={1} alignItems="flex-start" flex={1}>
                          <Image
                            src={`${serviceIcon}.svg`}
                            alt={name!}
                            width={30}
                            height={30}
                            unoptimized
                          />
                          <Stack flex={1}>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2">{name}</Typography>

                              {!gratis && price && (
                                <Typography
                                  variant="body2"
                                  fontWeight="fontWeightSemiBold"
                                  whiteSpace="nowrap"
                                >
                                  +{formatPrice(price, currency)}
                                </Typography>
                              )}
                              {gratis && (
                                <Typography
                                  variant="body2"
                                  fontWeight="fontWeightSemiBold"
                                  sx={{ color: 'success.main' }}
                                >
                                  {mandatory
                                    ? formatMessage(messages.articleServicesInclusive)
                                    : formatMessage(messages.articleServicesFree)}
                                </Typography>
                              )}
                            </Stack>
                            <Typography
                              role="button"
                              onClick={() => handleClick(service)}
                              variant="body3"
                              sx={{ color: 'text.darkTransparent', cursor: 'pointer' }}
                            >
                              {formatMessage(messages.articleServicesMoreInfo)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              ),
          )}
        </Stack>
        {activeArticleService && (
          <Dialog
            open={isModalOpen}
            handleClose={() => setIsModalOpen(false)}
            title={activeArticleService.name || ''}
            dialogContent={<ServiceLayer serviceLayerId={`a-plus_${activeArticleService.uuid}`} />}
          />
        )}
      </CardContent>
    </Card>
  );
};
